import { useEffect, useRef, useState } from "react";
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { Toast } from 'primereact/toast';
import { Card } from 'primereact/card';
import { useNavigate } from 'react-router-dom';
import StockService from '../../services/StockService';
import ProductService from '../../services/ProductService';
import WarehouseService from '../../services/WarehouseService';

export default function StockTransferPage() {
    const [mounted, setMounted] = useState(false);
    useEffect(() => { setMounted(true); }, []);
    const [products, setProducts] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        productId: null,
        sourceWarehouseId: null,
        targetWarehouseId: null,
        quantity: 0,
        description: ''
    });

    const toast = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        loadLookups();
    }, []);

    const loadLookups = async () => {
        try {
            const [prodRes, warRes] = await Promise.all([
                ProductService.getAll(),
                WarehouseService.getAll()
            ]);
            setProducts(prodRes.map(p => ({ label: p.name, value: p.id, barcode: p.barcode })));
            setWarehouses(warRes.data.map(w => ({ label: w.name, value: w.id })));
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Veriler yüklenemedi' });
        }
    };

    const handleTransfer = async () => {
        if (!formData.productId || !formData.sourceWarehouseId || !formData.targetWarehouseId || formData.quantity <= 0) {
            toast.current.show({ severity: 'warn', summary: 'Uyarı', detail: 'Lütfen tüm alanları eksiksiz doldurunuz.' });
            return;
        }

        if (formData.sourceWarehouseId === formData.targetWarehouseId) {
            toast.current.show({ severity: 'warn', summary: 'Hatalı Seçim', detail: 'Kaynak ve hedef depo aynı olamaz.' });
            return;
        }

        setLoading(true);
        try {
            const res = await StockService.transfer(formData);
            if (res.data.success) {
                toast.current.show({ severity: 'success', summary: 'Başarılı', detail: 'Transfer işlemi tamamlandı.' });
                setTimeout(() => navigate('/stock/movements'), 1200);
            } else {
                toast.current.show({ severity: 'error', summary: 'Hata', detail: res.data.message });
            }
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Hata', detail: err.response?.data?.message || 'Transfer sırasında bir hata oluştu.' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-2 md:p-6 animate-fade-in max-w-4xl mx-auto">
            <Toast ref={toast}  appendTo={mounted ? document.body : "self"} />
            <style>{`
                .dark .p-card {
                    background: #0f172a !important;
                    border: 1px solid #1e293b !important;
                    color: #f8fafc !important;
                }
                .dark .p-card-body, .dark .p-card-content {
                    background: #0f172a !important;
                    color: #f8fafc !important;
                }
                .dark .p-dropdown {
                    background: #1e293b !important;
                    border-color: #334155 !important;
                    color: white !important;
                }
                .dark .p-dropdown .p-dropdown-label {
                    color: white !important;
                }
                .dark .p-dropdown .p-dropdown-trigger {
                    color: #cbd5e1 !important;
                }
                .dark .p-dropdown-panel {
                    background: #0f172a !important;
                    border: 1px solid #1e293b !important;
                }
                .dark .p-dropdown-panel .p-dropdown-items .p-dropdown-item {
                    color: #cbd5e1 !important;
                }
                .dark .p-dropdown-panel .p-dropdown-items .p-dropdown-item:hover,
                .dark .p-dropdown-panel .p-dropdown-items .p-dropdown-item.p-highlight {
                    background: #1e293b !important;
                    color: #38bdf8 !important;
                }
                .dark .p-dropdown-filter-container {
                    background: #0f172a !important;
                    border-bottom: 1px solid #334155 !important;
                    padding: 0.5rem !important;
                }
                .dark .p-dropdown-filter-container .p-dropdown-filter {
                    background: #1e293b !important;
                    border-color: #334155 !important;
                    color: white !important;
                }
                .dark .p-dropdown-filter-icon {
                    color: #cbd5e1 !important;
                }
                .dark .p-inputtextarea {
                    background: #1e293b !important;
                    color: white !important;
                    border-color: #334155 !important;
                }
            `}</style>

            <div className="flex flex-col md:flex-row items-center justify-between mb-12 gap-6">
                <div className="flex items-center gap-6">
                    <div className="w-20 h-20 rounded-[2.5rem] bg-blue-600 flex items-center justify-center shadow-2xl border-4 border-white dark:border-slate-800">
                        <i className="pi pi-arrow-right-arrow-left text-white text-3xl" />
                    </div>
                    <div>
                        <h1 className="text-3xl md:text-4xl font-black text-slate-900 dark:text-white leading-none uppercase tracking-tighter">Depo Transferi</h1>
                        <p className="text-slate-500 dark:text-slate-400 font-bold text-xs uppercase tracking-[0.2em] mt-3 italic">Şubeler Arası Envanter Dağıtımı</p>
                    </div>
                </div>
            </div>

            <Card className="rounded-[3rem] border-none shadow-2xl shadow-slate-200/50 dark:shadow-none overflow-hidden relative bg-white dark:bg-slate-950 dark:border dark:border-slate-800">
                <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/5 rounded-full blur-3xl -mr-32 -mt-32" />

                <div className="p-4 md:p-10 space-y-10 relative z-10">

                    {/* Step 1: Product Selection */}
                    <div className="space-y-4">
                        <div className="flex items-center gap-3">
                            <span className="w-8 h-8 rounded-full bg-slate-900 dark:bg-slate-800 text-white dark:text-slate-200 flex items-center justify-center font-black text-xs">1</span>
                            <h3 className="text-sm font-black text-slate-800 dark:text-slate-200 uppercase tracking-widest m-0">ÜRÜN SEÇİMİ</h3>
                        </div>
                        <Dropdown
                            value={formData.productId}
                            options={products}
                            onChange={(e) => setFormData({ ...formData, productId: e.value })}
                            filter
                            placeholder="Transfer edilecek ürünü seçin..."
                            className="w-full p-3 border-2 border-slate-50 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900 rounded-2xl font-bold"
                            itemTemplate={(opt) => (
                                <div className="flex flex-col">
                                    <span className="font-bold text-slate-700 dark:text-slate-300">{opt.label}</span>
                                    <span className="text-[10px] text-slate-400 dark:text-slate-500 font-bold uppercase">{opt.barcode || 'Barkodsuz'}</span>
                                </div>
                            )}
                        />
                    </div>

                    {/* Step 2: Route Selection */}
                    <div className="grid grid-cols-1 md:grid-cols-11 gap-4 items-center">
                        <div className="md:col-span-5 space-y-4">
                             <div className="flex items-center gap-3">
                                <span className="w-8 h-8 rounded-full bg-slate-900 dark:bg-slate-800 text-white dark:text-slate-200 flex items-center justify-center font-black text-xs">2</span>
                                <h3 className="text-sm font-black text-slate-800 dark:text-slate-200 uppercase tracking-widest m-0">KAYNAK DEPO</h3>
                            </div>
                            <Dropdown
                                value={formData.sourceWarehouseId}
                                options={warehouses}
                                onChange={(e) => setFormData({ ...formData, sourceWarehouseId: e.value })}
                                placeholder="Nerden?"
                                className="w-full p-3 border-2 border-slate-50 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900 rounded-2xl font-bold"
                            />
                        </div>

                        <div className="md:col-span-1 flex justify-center pt-8">
                             <div className="w-12 h-12 rounded-full bg-blue-50 dark:bg-blue-950/30 text-blue-600 dark:text-blue-400 flex items-center justify-center shadow-inner">
                                <i className="pi pi-arrow-right font-bold" />
                             </div>
                        </div>

                        <div className="md:col-span-5 space-y-4">
                             <div className="flex items-center gap-3">
                                <span className="w-8 h-8 rounded-full bg-slate-900 dark:bg-slate-800 text-white dark:text-slate-200 flex items-center justify-center font-black text-xs">3</span>
                                <h3 className="text-sm font-black text-slate-800 dark:text-slate-200 uppercase tracking-widest m-0">HEDEF DEPO</h3>
                            </div>
                            <Dropdown
                                value={formData.targetWarehouseId}
                                options={warehouses}
                                onChange={(e) => setFormData({ ...formData, targetWarehouseId: e.value })}
                                placeholder="Nereye?"
                                className="w-full p-3 border-2 border-slate-50 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900 rounded-2xl font-bold"
                            />
                        </div>
                    </div>

                    {/* Step 3: Quantity & Note */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="md:col-span-1 space-y-4">
                            <div className="flex items-center gap-3">
                                <span className="w-8 h-8 rounded-full bg-slate-900 dark:bg-slate-800 text-white dark:text-slate-200 flex items-center justify-center font-black text-xs">4</span>
                                <h3 className="text-sm font-black text-slate-800 dark:text-slate-200 uppercase tracking-widest m-0">MİKTAR</h3>
                            </div>
                            <InputNumber
                                value={formData.quantity}
                                onValueChange={(e) => setFormData({ ...formData, quantity: e.value })}
                                min={0}
                                placeholder="0.00"
                                className="w-full"
                                inputClassName="p-5 bg-slate-900 dark:bg-slate-950 text-white rounded-3xl font-black text-3xl text-center shadow-2xl dark:shadow-none border dark:border-slate-800"
                            />
                        </div>
                        <div className="md:col-span-2 space-y-4">
                            <div className="flex items-center gap-3">
                                <span className="w-8 h-8 rounded-full bg-slate-900 dark:bg-slate-800 text-white dark:text-slate-200 flex items-center justify-center font-black text-xs">5</span>
                                <h3 className="text-sm font-black text-slate-800 dark:text-slate-200 uppercase tracking-widest m-0">TRANSFER NOTU</h3>
                            </div>
                            <InputTextarea
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                rows={2}
                                placeholder="Transfer nedeni veya açıklama..."
                                className="w-full p-4 bg-slate-50 dark:bg-slate-900 border-none dark:border dark:border-slate-800 rounded-2xl font-medium dark:text-white"
                            />
                        </div>
                    </div>

                    {/* Submit */}
                    <div className="pt-6">
                        <Button
                            label="TRANSFERİ BAŞLAT"
                            icon="pi pi-sync"
                            loading={loading}
                            className="w-full !bg-blue-600 border-none h-20 rounded-[2rem] font-black text-lg shadow-2xl dark:shadow-none transition-all hover:scale-[1.01] hover:!bg-slate-900 dark:hover:!bg-slate-800"
                            onClick={handleTransfer}
                        />
                    </div>
                </div>
            </Card>
        </div>
    );
}
